import type { Attributes, ExceptionContext, Span, SpanOptions } from '../types';
import { BaseLoggerProvider } from './base';

/**
 * Composite span that delegates to multiple spans
 */
class CompositeSpan implements Span {
  constructor(private spans: Span[]) { }

  setAttribute(key: string, value: any): void {
    this.spans.forEach((span) => span.setAttribute(key, value));
  }

  setAttributes(attributes: Attributes): void {
    this.spans.forEach((span) => span.setAttributes(attributes));
  }
}

/**
 * Composite logger provider that forwards to multiple providers
 * Useful for logging to multiple destinations (e.g., Console)
 */
export class CompositeProvider extends BaseLoggerProvider {
  constructor(private providers: BaseLoggerProvider[]) {
    super();
  }

  trace(message: string, attributes?: Attributes): void {
    this.providers.forEach((provider) => provider.trace(message, attributes));
  }

  debug(message: string, attributes?: Attributes): void {
    this.providers.forEach((provider) => provider.debug(message, attributes));
  }

  info(message: string, attributes?: Attributes): void {
    this.providers.forEach((provider) => provider.info(message, attributes));
  }

  warn(message: string, attributes?: Attributes): void {
    this.providers.forEach((provider) => provider.warn(message, attributes));
  }

  error(message: string, attributes?: Attributes): void {
    this.providers.forEach((provider) => provider.error(message, attributes));
  }

  fatal(message: string, attributes?: Attributes): void {
    this.providers.forEach((provider) => provider.fatal(message, attributes));
  }

  async startSpan<T>(
    name: string,
    options: SpanOptions,
    callback: (span: Span) => Promise<T>
  ): Promise<T> {
    // Start all provider spans
    const spans: Span[] = [];
    const compositeSpan = new CompositeSpan(spans);

    // Use the first provider as the "primary" for execution
    // but forward span operations to all providers
    const primaryProvider = this.providers[0];
    if (!primaryProvider) {
      // No providers available, execute callback directly with composite span
      return callback(compositeSpan);
    }

    return primaryProvider.startSpan(name, options, async (primarySpan) => {
      spans.push(primarySpan);

      // Start spans for other providers (but don't await their callbacks)
      for (let i = 1; i < this.providers.length; i++) {
        this.providers[i]
          ?.startSpan(name, options, async (span) => {
            spans.push(span);
            // Return a dummy promise that will be ignored
            return Promise.resolve(null);
          })
          .catch(() => { }); // Silently ignore errors from secondary providers
      }

      // Execute the actual callback with the composite span
      return callback(compositeSpan);
    });
  }

  captureException(error: Error, context?: ExceptionContext): void {
    this.providers.forEach((provider) =>
      provider.captureException(error, context)
    );
  }
}
